// src/interfaces/agentApiTypes.ts

import { createLogger } from "../utils/logger";

const log = createLogger("AgentApiTypes");

/**
 * Unified Chat Request Interface
 *
 * This interface represents what our UI components and services will work with.
 * It abstracts away the differences between agent-specific API requirements.
 *
 * Think of this as a common language that all parts of our app can speak,
 * regardless of which agent is currently active.
 */
export interface UnifiedChatRequest {
  // Core message from the user
  message: string;

  // The main document - this will ALWAYS be the currently open Word document
  // For red-line-analyzer: becomes 'full_file'
  // For contract-qa: becomes 'executed_agreement_file'
  documentFile: File;

  // Additional files for comparison (only used by contract-qa)
  // These are the templates uploaded via the UI
  additionalFiles?: File[];

  // Selected text from the document (common to both APIs)
  selectedTextFile?: File;

  // Session management
  sessionId?: string;

  // Document caching
  documentId?: string;

  // Performance optimization
  bypassCache?: boolean;

  // Enhanced features configuration (thinking, web search, contract_type, etc.)
  details?: {
    thinking?: boolean;
    web_search?: boolean;
    contract_type?: string;
    track_changes_mode?: string; // "off", "trackAll", "trackMineOnly", or omitted if unknown
    markup_mode?: string; // "Balloon", "Inline", "Mixed", or omitted if unknown
    quick_analysis?: boolean; // true when the add-in's Quick Analysis action sent this turn; omitted otherwise
    [key: string]: any;
  };

  // Conversation history for context
  chatHistory?: any[];

  // Access token for authentication
  accessToken?: string;
}

/**
 * Unified Summary Request Interface
 *
 * Similar abstraction for the summary API endpoints.
 * This allows us to use the same interface regardless of agent.
 */
export interface UnifiedSummaryRequest {
  // The main document (currently open in Word)
  // For red-line-analyzer: becomes 'file'
  // For contract-qa: becomes 'executed_agreement_file'
  documentFile: File;

  // Additional template files (only for contract-qa)
  additionalFiles?: File[];

  // Request configuration
  sessionId?: string;
  bypassCache?: boolean;
  details?: {
    thinking?: boolean;
    web_search?: boolean;
    contract_type?: string;
    track_changes_mode?: string; // "off", "trackAll", "trackMineOnly", or omitted if unknown
    markup_mode?: string; // "Balloon", "Inline", "Mixed", or omitted if unknown
    [key: string]: any;
  };
  documentId?: string;
  accessToken?: string;
}

/**
 * Agent API Configuration
 *
 * This defines what each agent needs to know about its API endpoints
 * and how to transform unified requests into agent-specific formats.
 */
export interface AgentApiConfig {
  // Endpoint paths (relative to base URL)
  chatEndpoint: string;
  /**
   * @deprecated RETIRED. The `/summary/*` routes are no longer mounted on the
   * backend and the Analysis tab that drove them is gone. Nothing reads this
   * at runtime - the real routing has always been the switch in
   * agentAwareApiAdapter - so it survives only as a record of where the
   * summary path used to point.
   */
  summaryEndpoint: string;

  // Field mappings - how to transform unified fields to API-specific fields
  fieldMappings: {
    chat: {
      documentFileParam: string; // 'full_file' or 'executed_agreement_file'
      additionalFileParam?: string; // 'additional_file' for contract-qa
    };
    /** @deprecated RETIRED with `summaryEndpoint` above. */
    summary: {
      documentFileParam: string; // 'file' or 'executed_agreement_file'
      additionalFileParam?: string; // 'additional_file' for contract-qa
    };
  };

  // Feature support flags
  supportsAdditionalFiles: boolean;

  // Session configuration
  sessionPrefix: string; // e.g., 'redline_' or 'contract_qa_'
}

/**
 * Get agent-specific API configuration
 *
 * This function returns the configuration for each agent,
 * defining how to interact with their respective APIs.
 */
export function getAgentApiConfig(agentId: string): AgentApiConfig {
  const configs: Record<string, AgentApiConfig> = {
    "ccm-space": {
      chatEndpoint: "/chat/general_agent/stream/",
      summaryEndpoint: "", // No summary endpoint for CCM Space
      fieldMappings: {
        chat: {
          documentFileParam: "full_text_file",
          additionalFileParam: "additional_context", // Supports multiple files
        },
        summary: {
          documentFileParam: "", // Not applicable
        },
      },
      supportsAdditionalFiles: true,
      sessionPrefix: "ccm_space_",
    },

    "red-line-analyzer": {
      chatEndpoint: "/chat/redline_analyzer/stream/",
      summaryEndpoint: "/summary/redline_analyzer/stream/",
      fieldMappings: {
        chat: {
          documentFileParam: "full_file",
        },
        summary: {
          documentFileParam: "file",
        },
      },
      supportsAdditionalFiles: false,
      sessionPrefix: "redline_",
    },

    "contract-qa": {
      chatEndpoint: "/chat/executed_contracts/stream/",
      summaryEndpoint: "/summary/executed_contracts/stream/",
      fieldMappings: {
        chat: {
          documentFileParam: "executed_agreement_file",
          additionalFileParam: "additional_file",
        },
        summary: {
          documentFileParam: "executed_agreement_file",
          additionalFileParam: "additional_file",
        },
      },
      supportsAdditionalFiles: true,
      sessionPrefix: "contract_qa_",
    },

    "drafting-assistant": {
      // Uses document generation endpoints
      chatEndpoint: "/chat/document_generator/stream/",
      summaryEndpoint: "", // No summary endpoint for drafting assistant
      fieldMappings: {
        chat: {
          documentFileParam: "full_text_file",
          additionalFileParam: "additional_context", // Supports multiple files
        },
        summary: {
          documentFileParam: "", // Not applicable
        },
      },
      supportsAdditionalFiles: true,
      sessionPrefix: "drafting_",
    },
  };

  const config = configs[agentId];

  if (!config) {
    log.warn(`No API configuration found for agent: ${agentId}, falling back to red-line-analyzer`);
    return configs["red-line-analyzer"];
  }

  return config;
}

/**
 * Generate agent-specific session ID
 *
 * Each agent maintains its own session to keep conversations separate.
 * This prevents context bleeding between different agent types.
 */
export function generateAgentSessionId(agentId: string): string {
  const config = getAgentApiConfig(agentId);
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substring(2, 8);

  return `${config.sessionPrefix}${timestamp}_${randomSuffix}`;
}

/**
 * Type guard to check if an agent supports additional files
 */
export function agentSupportsAdditionalFiles(agentId: string): boolean {
  return getAgentApiConfig(agentId).supportsAdditionalFiles;
}
